"use client";

import React, { useState, ChangeEvent } from "react";
import {
  FileText,
  Briefcase,
  MapPin,
  Bell,
  Lock,
  ShieldCheck,
  ChevronRight,
  LogOut,
  Trash2,
  CheckCircle2,
  X,
  Upload,
  Building2,
  Clock,
  PhoneCall,
  Download,
} from "lucide-react";

type ModalType =
  | "profile"
  | "career"
  | "password"
  | "block-companies"
  | "resume"
  | null;

type JobSearchStatus = "actively_looking" | "casually_looking" | "not_looking";

interface UserProfile {
  name: string;
  email: string;
  phone: string;
  role: string;
  experience: string;
  location: string;
  currentSalary: string;
  noticePeriod: string;
}

interface CareerPref {
  role: string;
  location: string;
  salary: string;
  mode: "Office" | "Hybrid" | "Remote";
}

export default function SettingsPage() {
  const [modal, setModal] = useState<ModalType>(null);
  const [searchStatus, setSearchStatus] =
    useState<JobSearchStatus>("actively_looking");

  // Privacy & Controls
  const [resumeVisible, setResumeVisible] = useState(true);
  const [jobAlerts, setJobAlerts] = useState(true);
  const [whatsappAlerts, setWhatsappAlerts] = useState(true);
  const [recruiterCalls, setRecruiterCalls] = useState(true);
  const [profileAccess, setProfileAccess] = useState(true);

  // Blocked Companies List
  const [blockedCompanies, setBlockedCompanies] = useState<string[]>([
    "TCS",
    "Infosys",
  ]);

  // Resume Info
  const [resume, setResume] = useState({
    name: "Rahul_Sharma_Resume.pdf",
    updatedAt: "10 Feb 2026",
    size: "1.2 MB",
  });

  // User Profile
  const [user, setUser] = useState<UserProfile>({
    name: "Rahul Sharma",
    role: "Frontend Developer",
    experience: "3 Years Experience",
    location: "Noida",
    email: "rahul@gmail.com",
    phone: "+91 9876543210",
    currentSalary: "6.5 LPA",
    noticePeriod: "15 Days or less",
  });

  // Career Preferences
  const [career, setCareer] = useState<CareerPref>({
    role: "Frontend Developer",
    location: "Noida, Delhi NCR",
    salary: "9 - 12 LPA",
    mode: "Hybrid",
  });

  return (
    <div className="mx-auto max-w-5xl space-y-3 px-2 py-3 xs:px-4 xs:py-4 sm:space-y-6 sm:px-6 sm:py-6 lg:px-8">
      {/* HEADER SECTION */}
      <div className="flex flex-col gap-3 xs:gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-bold tracking-tight text-slate-900 xs:text-xl sm:text-2xl">
            Account & Preferences
          </h1>
          <p className="mt-0.5 text-[11px] text-slate-500 xs:text-xs sm:mt-1 sm:text-sm">
            Manage profile visibility, recruiter contacts, and alerts.
          </p>
        </div>

        <div className="flex w-full shrink-0 items-center gap-2.5 rounded-xl border border-blue-100 bg-blue-50/70 p-2.5 self-start xs:p-3 sm:w-auto sm:self-auto sm:rounded-2xl sm:px-4 sm:py-3">
          <CheckCircle2 size={20} className="shrink-0 text-blue-600" />
          <div className="min-w-0">
            <p className="text-[10px] font-semibold text-slate-500 xs:text-[11px] sm:text-xs">
              Profile Strength
            </p>
            <p className="text-xs font-bold text-blue-600 xs:text-sm">
              92% Completed
            </p>
          </div>
        </div>
      </div>

      {/* JOB SEARCH STATUS SWITCHER */}
      <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50/30 p-3 xs:p-4 sm:rounded-2xl sm:p-5">
        <div className="mb-1 flex items-center gap-1.5 sm:mb-2">
          <Clock size={15} className="shrink-0 text-blue-600" />
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-blue-900 xs:text-xs">
            Job Search Status
          </h2>
        </div>
        <p className="mb-2.5 text-[11px] text-slate-600 xs:text-xs sm:mb-4">
          Recruiters filter candidates based on how actively they are looking.
        </p>

        <div className="grid grid-cols-1 gap-2 xs:gap-2.5 sm:gap-3 md:grid-cols-3">
          <StatusCard
            active={searchStatus === "actively_looking"}
            onClick={() => setSearchStatus("actively_looking")}
            title="Actively Looking"
            desc="Ready for interviews & immediate joining"
            badge="Recommended"
          />
          <StatusCard
            active={searchStatus === "casually_looking"}
            onClick={() => setSearchStatus("casually_looking")}
            title="Casually Looking"
            desc="Open for good opportunities only"
          />
          <StatusCard
            active={searchStatus === "not_looking"}
            onClick={() => setSearchStatus("not_looking")}
            title="Not Looking"
            desc="Hide profile from recruiter searches"
          />
        </div>
      </div>

      {/* PROFILE SUMMARY CARD */}
      <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-xs xs:p-4 sm:rounded-2xl sm:p-5">
        <div className="flex flex-col gap-3 xs:gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 sm:items-center">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-600 xs:h-12 xs:w-12 xs:text-base sm:h-14 sm:w-14 sm:text-lg">
              {user.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <h2 className="truncate text-sm font-bold text-slate-900 xs:text-base sm:text-lg">
                  {user.name}
                </h2>
                <span className="whitespace-nowrap rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold text-slate-600 xs:text-[10px]">
                  Notice: {user.noticePeriod}
                </span>
              </div>
              <p className="text-xs font-semibold text-blue-600">{user.role}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] font-medium text-slate-500 xs:text-xs">
                <span>{user.experience}</span>
                <span>•</span>
                <span>{user.location}</span>
                <span>•</span>
                <span className="truncate">CTC: {user.currentSalary}</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setModal("profile")}
            className="w-full shrink-0 rounded-xl bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white transition hover:bg-blue-700 active:scale-98 xs:py-2.5 sm:w-auto"
          >
            Edit Profile
          </button>
        </div>
      </div>

      {/* RESUME MANAGEMENT */}
      <Section title="Resume & Documents" icon={FileText}>
        <div className="flex flex-col justify-between gap-3 px-3.5 py-3.5 xs:flex-row xs:items-center xs:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 xs:h-10 xs:w-10">
              <FileText size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-xs font-bold text-slate-900">
                {resume.name}
              </h3>
              <p className="truncate text-[10px] font-medium text-slate-500 xs:text-[11px]">
                Uploaded {resume.updatedAt} • {resume.size}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 self-end xs:self-auto">
            <button
              type="button"
              onClick={() => setModal("resume")}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 active:bg-slate-100"
            >
              <Upload size={14} /> Update
            </button>
            <button
              type="button"
              className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 active:bg-slate-100"
            >
              <Download size={14} />
            </button>
          </div>
        </div>

        <Row
          icon={ShieldCheck}
          title="Resume Visibility"
          desc={
            resumeVisible
              ? "Recruiters can view & download your CV"
              : "Resume hidden from direct download"
          }
          toggle
          value={resumeVisible}
          onToggle={() => setResumeVisible(!resumeVisible)}
        />
      </Section>

      {/* CAREER PREFERENCES */}
      <Section title="Job & Salary Preferences" icon={Briefcase}>
        <Row
          icon={Briefcase}
          title="Preferred Role & Work Mode"
          desc={`${career.role} (${career.mode})`}
          onClick={() => setModal("career")}
        />
        <Row
          icon={MapPin}
          title="Location & Expected CTC"
          desc={`${career.location} • Expected: ${career.salary}`}
          onClick={() => setModal("career")}
        />
      </Section>

      {/* PRIVACY & BLOCK COMPANIES */}
      <Section title="Privacy & Block Settings" icon={Building2}>
        <Row
          icon={Building2}
          title="Blocked Companies"
          desc={`${blockedCompanies.length} companies blocked`}
          onClick={() => setModal("block-companies")}
        />
        <Row
          icon={ShieldCheck}
          title="Recruiter Search Visibility"
          desc={
            profileAccess
              ? "Searchable by verified recruiters"
              : "Hidden from search results"
          }
          toggle
          value={profileAccess}
          onToggle={() => setProfileAccess(!profileAccess)}
        />
      </Section>

      {/* COMMUNICATIONS */}
      <Section title="Notifications & Alerts" icon={Bell}>
        <Row
          icon={Bell}
          title="Email Job Alerts"
          desc="Daily job recommendations in email"
          toggle
          value={jobAlerts}
          onToggle={() => setJobAlerts(!jobAlerts)}
        />
        <Row
          icon={PhoneCall}
          title="WhatsApp Updates"
          desc="Interview schedules & recruiter chats"
          toggle
          value={whatsappAlerts}
          onToggle={() => setWhatsappAlerts(!whatsappAlerts)}
        />
        <Row
          icon={PhoneCall}
          title="Recruiter Direct Calls"
          desc="Allow calls on mobile phone"
          toggle
          value={recruiterCalls}
          onToggle={() => setRecruiterCalls(!recruiterCalls)}
        />
      </Section>

      {/* SECURITY */}
      <Section title="Security" icon={Lock}>
        <Row
          icon={Lock}
          title="Change Password"
          desc="Update security credentials"
          onClick={() => setModal("password")}
        />
      </Section>

      {/* DANGER ZONE */}
      <div className="rounded-xl border border-red-200 bg-red-50/50 p-3.5 xs:p-4 sm:rounded-2xl sm:p-5">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-red-700 xs:text-xs">
          Account Actions
        </h2>
        <div className="mt-2.5 grid grid-cols-1 gap-2 xs:grid-cols-2 xs:gap-3">
          <button
            type="button"
            onClick={() =>
              confirm("Are you sure you want to logout?") && alert("Logged out!")
            }
            className="flex items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-xs font-semibold text-white hover:bg-black active:scale-98"
          >
            <LogOut size={15} /> Logout
          </button>

          <button
            type="button"
            onClick={() =>
              confirm("Delete account permanently?") && alert("Requested!")
            }
            className="flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-white py-2.5 text-xs font-semibold text-red-600 hover:bg-red-50 active:scale-98"
          >
            <Trash2 size={15} /> Delete Account
          </button>
        </div>
      </div>

      {/* MODALS CONTAINER */}
      {modal && (
        <Modal close={() => setModal(null)}>
          {modal === "profile" && (
            <ProfileForm
              user={user}
              onSave={(updated) => {
                setUser(updated);
                setModal(null);
              }}
              close={() => setModal(null)}
            />
          )}

          {modal === "career" && (
            <CareerForm
              career={career}
              onSave={(updated) => {
                setCareer(updated);
                setModal(null);
              }}
              close={() => setModal(null)}
            />
          )}

          {modal === "block-companies" && (
            <BlockCompaniesModal
              list={blockedCompanies}
              onSave={(updated) => {
                setBlockedCompanies(updated);
                setModal(null);
              }}
              close={() => setModal(null)}
            />
          )}

          {modal === "resume" && (
            <ResumeModal
              onResumeUpload={(fileName) => {
                setResume({
                  name: fileName,
                  updatedAt: "Just Now",
                  size: "1.5 MB",
                });
                setModal(null);
              }}
              close={() => setModal(null)}
            />
          )}

          {modal === "password" && (
            <PasswordForm close={() => setModal(null)} />
          )}
        </Modal>
      )}
    </div>
  );
}

/* ==========================================================================
   SUB-COMPONENTS
   ========================================================================== */

function StatusCard({
  active,
  onClick,
  title,
  desc,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  desc: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-col justify-between rounded-xl border p-3 text-left transition ${
        active
          ? "border-blue-600 bg-white shadow-xs ring-2 ring-blue-100"
          : "border-slate-200 bg-white/70 hover:bg-white active:bg-slate-50"
      }`}
    >
      {badge && (
        <span className="absolute right-2 top-2 rounded-md bg-blue-100 px-1.5 py-0.5 text-[8px] font-bold text-blue-700 xs:text-[9px]">
          {badge}
        </span>
      )}
      <div className="pr-10 xs:pr-12 sm:pr-0">
        <p className={`text-xs font-bold ${active ? "text-blue-600" : "text-slate-800"}`}>
          {title}
        </p>
        <p className="mt-0.5 text-[10px] leading-tight text-slate-500 xs:text-[11px]">
          {desc}
        </p>
      </div>
    </button>
  );
}

function ProfileForm({
  user,
  onSave,
  close,
}: {
  user: UserProfile;
  onSave: (updated: UserProfile) => void;
  close: () => void;
}) {
  const [form, setForm] = useState<UserProfile>(user);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <FormHeader
        title="Edit Profile Details"
        desc="Update your details for recruiter views"
      />
      <div className="space-y-3 p-4 sm:space-y-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          <Input
            label="Full Name"
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
          />
          <Input
            label="Email Address"
            type="email"
            value={form.email}
            onChange={(v) => setForm({ ...form, email: v })}
          />
          <Input
            label="Phone Number"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
          />
          <Input
            label="Current Role"
            value={form.role}
            onChange={(v) => setForm({ ...form, role: v })}
          />
          <Input
            label="Current CTC"
            value={form.currentSalary}
            onChange={(v) => setForm({ ...form, currentSalary: v })}
          />
          <Input
            label="Notice Period"
            value={form.noticePeriod}
            onChange={(v) => setForm({ ...form, noticePeriod: v })}
          />
        </div>

        <FormActions close={close} submitText="Save Profile" />
      </div>
    </form>
  );
}

function CareerForm({
  career,
  onSave,
  close,
}: {
  career: CareerPref;
  onSave: (updated: CareerPref) => void;
  close: () => void;
}) {
  const [form, setForm] = useState<CareerPref>(career);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <FormHeader
        title="Career Preferences"
        desc="Specify role and location requirements"
      />
      <div className="space-y-3 p-4 sm:space-y-4 sm:p-6">
        <Input
          label="Preferred Role"
          value={form.role}
          onChange={(v) => setForm({ ...form, role: v })}
        />
        <Input
          label="Preferred Location"
          value={form.location}
          onChange={(v) => setForm({ ...form, location: v })}
        />
        <Input
          label="Expected CTC"
          value={form.salary}
          onChange={(v) => setForm({ ...form, salary: v })}
        />

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-slate-700">
            Work Mode
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(["Office", "Hybrid", "Remote"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setForm({ ...form, mode })}
                className={`rounded-xl py-2 text-xs font-semibold transition ${
                  form.mode === mode
                    ? "bg-blue-600 text-white"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        <FormActions close={close} submitText="Save Preferences" />
      </div>
    </form>
  );
}

function BlockCompaniesModal({
  list,
  onSave,
  close,
}: {
  list: string[];
  onSave: (updated: string[]) => void;
  close: () => void;
}) {
  const [companies, setCompanies] = useState<string[]>(list);
  const [input, setInput] = useState("");

  const addCompany = () => {
    if (input.trim() && !companies.includes(input.trim())) {
      setCompanies([...companies, input.trim()]);
      setInput("");
    }
  };

  return (
    <div>
      <FormHeader
        title="Block Companies"
        desc="Prevent specific employers from viewing your profile"
      />
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter company name"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs outline-none focus:border-blue-500"
          />
          <button
            type="button"
            onClick={addCompany}
            className="shrink-0 rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white active:bg-black"
          >
            Block
          </button>
        </div>

        <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
          {companies.map((comp) => (
            <span
              key={comp}
              className="flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700"
            >
              {comp}
              <button
                type="button"
                onClick={() => setCompanies(companies.filter((c) => c !== comp))}
                className="p-0.5 text-slate-400 hover:text-red-500"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>

        <div className="flex flex-col-reverse gap-2 pt-2 xs:flex-row">
          <button
            type="button"
            onClick={close}
            className="w-full rounded-xl border py-2.5 text-xs font-semibold active:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(companies)}
            className="w-full rounded-xl bg-blue-600 py-2.5 text-xs font-semibold text-white active:bg-blue-700"
          >
            Save Blocklist
          </button>
        </div>
      </div>
    </div>
  );
}

function ResumeModal({
  onResumeUpload,
  close,
}: {
  onResumeUpload: (fileName: string) => void;
  close: () => void;
}) {
  const [fileName, setFileName] = useState("");

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFileName(e.target.files[0].name);
    }
  };

  return (
    <div>
      <FormHeader
        title="Upload Resume"
        desc="Formats: PDF, DOCX (Max size 2MB)"
      />
      <div className="space-y-4 p-4 sm:p-6">
        <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-5 text-center transition hover:bg-slate-50 active:bg-slate-100">
          <Upload size={28} className="mb-1.5 text-blue-600" />
          <span className="text-xs font-bold text-slate-700">
            Tap to upload file
          </span>
          <span className="mt-0.5 text-[10px] text-slate-400">
            PDF, DOCX up to 2MB
          </span>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="hidden"
          />
        </label>

        {fileName && (
          <div className="flex items-center gap-2 rounded-xl bg-blue-50 p-2.5 text-xs font-semibold text-blue-700">
            <CheckCircle2 size={16} className="shrink-0" />
            <span className="truncate">{fileName}</span>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 pt-2 xs:flex-row">
          <button
            type="button"
            onClick={close}
            className="w-full rounded-xl border py-2.5 text-xs font-semibold active:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!fileName}
            onClick={() => onResumeUpload(fileName)}
            className="w-full rounded-xl bg-blue-600 py-2.5 text-xs font-semibold text-white disabled:opacity-50 active:bg-blue-700"
          >
            Save Resume
          </button>
        </div>
      </div>
    </div>
  );
}

function PasswordForm({ close }: { close: () => void }) {
  const [pass, setPass] = useState({ old: "", new: "", confirm: "" });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        alert("Password updated!");
        close();
      }}
    >
      <FormHeader title="Change Password" desc="Keep your account secure" />
      <div className="space-y-3 p-4 sm:space-y-4 sm:p-6">
        <Input
          label="Current Password"
          type="password"
          value={pass.old}
          onChange={(v) => setPass({ ...pass, old: v })}
        />
        <Input
          label="New Password"
          type="password"
          value={pass.new}
          onChange={(v) => setPass({ ...pass, new: v })}
        />
        <Input
          label="Confirm Password"
          type="password"
          value={pass.confirm}
          onChange={(v) => setPass({ ...pass, confirm: v })}
        />

        <FormActions close={close} submitText="Update Password" />
      </div>
    </form>
  );
}

/* ==========================================================================
   ATOMIC UI COMPONENTS
   ========================================================================== */

function Modal({
  children,
  close,
}: {
  children: React.ReactNode;
  close: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 backdrop-blur-xs xs:p-2 sm:items-center sm:p-4">
      <div className="relative max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200 xs:rounded-2xl">
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 z-10 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 active:bg-slate-200"
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

function FormHeader({ title, desc }: { title: string; desc: string }) {
  return (
    <div className="border-b border-slate-100 p-4 pb-3 pr-10 sm:p-6 sm:pb-4">
      <h2 className="text-base font-bold text-slate-900 sm:text-lg">{title}</h2>
      <p className="mt-0.5 text-xs text-slate-500">{desc}</p>
    </div>
  );
}

function FormActions({
  close,
  submitText,
}: {
  close: () => void;
  submitText: string;
}) {
  return (
    <div className="flex flex-col-reverse gap-2 pt-3 xs:flex-row">
      <button
        type="button"
        onClick={close}
        className="w-full rounded-xl border border-slate-200 py-2.5 text-xs font-semibold text-slate-600 active:bg-slate-100"
      >
        Cancel
      </button>
      <button
        type="submit"
        className="w-full rounded-xl bg-blue-600 py-2.5 text-xs font-semibold text-white active:bg-blue-700"
      >
        {submitText}
      </button>
    </div>
  );
}

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs sm:rounded-2xl">
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-3.5 py-3 xs:px-5">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-100/70 text-blue-600">
          <Icon size={15} />
        </div>
        <h2 className="text-xs font-bold text-slate-900">{title}</h2>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </div>
  );
}

function Row({
  icon: Icon,
  title,
  desc,
  onClick,
  toggle = false,
  value,
  onToggle,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  onClick?: () => void;
  toggle?: boolean;
  value?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2.5 px-3.5 py-3 transition hover:bg-slate-50/50 xs:px-5 sm:py-3.5">
      <div className="flex min-w-0 items-center gap-2.5 xs:gap-3.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 xs:h-9 xs:w-9">
          <Icon size={16} />
        </div>
        <div className="min-w-0">
          <h3 className="truncate text-xs font-bold leading-tight text-slate-900">
            {title}
          </h3>
          <p className="mt-0.5 truncate text-[10px] font-medium text-slate-500 xs:text-[11px]">
            {desc}
          </p>
        </div>
      </div>

      {toggle ? (
        <button
          type="button"
          onClick={onToggle}
          className={`relative h-5 w-9 shrink-0 rounded-full transition-colors xs:h-6 xs:w-11 ${
            value ? "bg-blue-600" : "bg-slate-300"
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-md transition xs:h-4 xs:w-4 ${
              value ? "translate-x-4 xs:translate-x-5" : "translate-x-1"
            }`}
          />
        </button>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 active:bg-slate-200"
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
}